import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import Papa from 'papaparse';
import useCSVNumberBlocksUpdate from 'hooks/useCSVNumberBlocksUpdate';
import VerifyCSVView from 'pages/accounts/users/csv/VerifyCSVView';
import VerifyCSVTableView from 'pages/accounts/users/csv/VerifyCSVTableView';
import {
    Box,
    HStack,
    Select,
    Text,
    UnorderedList,
    ListItem,
} from '@chakra-ui/react';
import {
    DownloadIcon,
    ChevronRightIcon,
    AttachmentIcon,
    CloseIcon,
} from '@chakra-ui/icons';
import useCSVNumberBlocksBulkUpdate from 'hooks/useCSVNumberBlocksUpdate/useCSVNumberBlocksBulkUpdate';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import {
    succeedAPIColumns,
    failedAPIColumns,
    passedColumns,
    failedColumns,
} from 'hooks/useCSVNumberBlocksUpdate/constants';
import APILoadingView from 'pages/accounts/users/csv/APILoadingView';
import APIResultsView from 'pages/accounts/users/csv/APIResultsView';
import { axios } from 'services/axios';
import Button from 'components/v4/Button';
import Heading from 'components/v4/Heading';

const useCSVNumberBlocksUpdateUIControl = () => {
    const [viewVerificationResults, setViewVerificationResults] =
        useState(false);
    const [viewAPIResults, setViewAPIResults] = useState(false);
    const [loadingIndicator, setLoadingIndicator] = useState(false);
    const [showingAPIProgress, setShowingAPIProgress] = useState(false);

    const handleViewVerificationResults = () => {
        setViewVerificationResults(true);
        setViewAPIResults(false);
    };

    const handleCancel = () => {
        setLoadingIndicator(true);
        window.location.reload();
    };

    const handleViewAPIResults = () => {
        setViewAPIResults(true);
        setViewVerificationResults(false);
        setShowingAPIProgress(false);
    };

    const handleStartAPIProcess = () => {
        setShowingAPIProgress(true);
    };

    return {
        viewVerificationResults,
        handleViewVerificationResults,
        handleCancel,
        loadingIndicator,
        handleViewAPIResults,
        viewAPIResults,
        showingAPIProgress,
        handleStartAPIProcess,
    };
};

// Wrapper component that adds export type selection before CSV template
const NumberBlocksCSVTemplateView = ({
    handleDownloadTemplate,
    handleUploadUsersData,
    loading,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { currentPartner, currentWholesaler } = useSelector(
        (state) => state.navigationLists,
    );
    const { currentScope } = useSelector((state) => state.login);

    const [exportType, setExportType] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedCompanyName, setSelectedCompanyName] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [csvRowCount, setCsvRowCount] = useState(null);

    const { data: companies = [], isFetching: loadingCompanies } = useQuery({
        queryKey: [
            'numberBlockCsvCompanies',
            currentScope,
            currentPartner,
            currentWholesaler,
        ],
        queryFn: async () => {
            if (currentScope === 40 && currentPartner) {
                const res = await axios.get('/Companies/DropDown', {
                    params: { partnerID: currentPartner },
                    headers: { 'X-RequestScope': '40' },
                });
                return res?.data || [];
            }

            if (currentScope === 60 && currentWholesaler) {
                const res = await axios.get('/Companies/DropDown', {
                    params: { wholesalerID: currentWholesaler },
                    headers: { 'X-RequestScope': '60' },
                });
                return res?.data || [];
            }

            if (currentScope === 80) {
                const res = await axios.get('/Companies/DropDown', {
                    headers: { 'X-RequestScope': '80' },
                });
                return res?.data || [];
            }

            return [];
        },
        enabled:
            (currentScope === 40 && Boolean(currentPartner)) ||
            (currentScope === 60 && Boolean(currentWholesaler)) ||
            currentScope === 80,
        refetchOnWindowFocus: false,
    });

    const handleExportTypeChange = (e) => {
        const value = e.target.value;
        setExportType(value);
        if (value === 'unassigned') {
            setSelectedCompany('');
            setSelectedCompanyName('');
        }
    };

    const handleCompanyChange = (e) => {
        const companyId = e.target.value;
        setSelectedCompany(companyId);
        const company = companies.find((c) => c.id === companyId);
        setSelectedCompanyName(company?.name || '');
    };

    const handleDownloadClick = () => {
        if (exportType === 'company' && selectedCompany) {
            handleDownloadTemplate(
                'company',
                selectedCompany,
                selectedCompanyName,
            );
        } else if (exportType === 'unassigned') {
            handleDownloadTemplate('unassigned', null, null);
        }
    };

    const showTemplateSteps =
        (exportType === 'company' && selectedCompany) ||
        exportType === 'unassigned';

    // Custom wrapper to inject export type and company selection into CSVTemplateView
    const CustomCSVTemplateView = () => {
        return (
            <Box display="flex" justifyContent="start" width="100%">
                <Box
                    width="100%"
                    maxWidth="700px"
                    display="flex"
                    flexDirection="column"
                    gap="0"
                    fontFamily="Inter"
                    boxShadow="base"
                    p="8"
                    bg={darkMode ? 'dark.tcap' : 'white'}
                    rounded="md">
                    <Heading size="lg" mb={2}>
                        CSV Upload - Number Blocks
                    </Heading>

                    {/* Export Type Selection */}
                    <Box mt="10px">
                        <Text size="md">Select Export Type</Text>
                        <Select
                            placeholder="Select export type"
                            value={exportType}
                            onChange={handleExportTypeChange}
                            color={darkMode ? 'dark.tcap' : ''}
                            mt="20px">
                            <option value="company">
                                Export blocks assigned to a specific company
                            </option>
                            <option value="unassigned">
                                Export all blocks NOT assigned to any company
                            </option>
                        </Select>
                    </Box>

                    {/* Company Selection (shown only when company export type is selected) */}
                    {exportType === 'company' && (
                        <Box mt="20px">
                            <Text size="md">Select Company</Text>
                            <Select
                                placeholder="Choose a company..."
                                value={selectedCompany}
                                onChange={handleCompanyChange}
                                isDisabled={loadingCompanies}
                                color={darkMode ? 'dark.tcap' : ''}
                                mt="20px">
                                {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {/* Show template steps only when selections are complete */}
                    {showTemplateSteps && (
                        <>
                            {/* Description Text */}
                            <Box
                                p={4}
                                bg="blue.50"
                                borderRadius="md"
                                border="1px"
                                borderColor="blue.200"
                                borderLeft="5px solid"
                                borderLeftColor="blue.500"
                                color="blue.800"
                                mt={4}>
                                <Text fontSize="sm">
                                    Update number blocks to add or remove from
                                    companies, or remove from TCAP entirely.
                                    Please select the filter above to choose to
                                    export specific company numbers or all
                                    unassigned. Please note, all numbers under a
                                    block must not be assigned to any users or
                                    trunks prior to update.
                                </Text>
                            </Box>

                            <Text
                                mt="15px"
                                fontSize="md"
                                color="gray.600"
                                mb={1}>
                                Download the template, review the rules, and
                                upload your file.
                            </Text>

                            {/* Step 1 - Download */}
                            <Box mt="25px" mb={6}>
                                <Text fontWeight="bold" mb={2}>
                                    1. Download the CSV template
                                </Text>
                                <Button
                                    isLoading={loading}
                                    rightIcon={<DownloadIcon />}
                                    sx={{ width: '220px' }}
                                    size="sm"
                                    onClick={handleDownloadClick}>
                                    Download
                                </Button>
                            </Box>

                            {/* Step 2 - Upload */}
                            <Box mb={6}>
                                <Text fontWeight="bold" mb={2}>
                                    2. Upload CSV to be imported
                                </Text>
                                {uploadedFile ? (
                                    <Box
                                        border="2px solid"
                                        borderColor="green.400"
                                        borderRadius="md"
                                        p={5}
                                        bg="green.50"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={2}>
                                        <HStack spacing={3}>
                                            <AttachmentIcon
                                                boxSize={5}
                                                color="green.500"
                                            />
                                            <Box>
                                                <Text
                                                    color="green.700"
                                                    fontWeight="semibold"
                                                    fontSize="sm">
                                                    {uploadedFile.name}
                                                </Text>
                                                <Text
                                                    fontSize="xs"
                                                    color="green.600">
                                                    Valid CSV
                                                    {csvRowCount !== null
                                                        ? ` • ${csvRowCount} Rows`
                                                        : ''}
                                                </Text>
                                            </Box>
                                        </HStack>
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            leftIcon={<CloseIcon boxSize={2} />}
                                            onClick={() => {
                                                setUploadedFile(null);
                                                setCsvRowCount(null);
                                            }}>
                                            Remove
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box
                                        border="2px dashed"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        p={8}
                                        textAlign="center"
                                        bg="gray.50"
                                        cursor="pointer"
                                        transition="border-color 0.2s, background 0.2s"
                                        mb={2}
                                        onClick={() => {
                                            const input =
                                                document.createElement('input');
                                            input.type = 'file';
                                            input.accept = '.csv';
                                            input.onchange = (e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    setUploadedFile(file);
                                                    Papa.parse(file, {
                                                        header: true,
                                                        skipEmptyLines: true,
                                                        complete: (results) => {
                                                            setCsvRowCount(
                                                                results.data
                                                                    .length,
                                                            );
                                                        },
                                                    });
                                                }
                                            };
                                            input.click();
                                        }}>
                                        <AttachmentIcon
                                            boxSize={10}
                                            color="gray.400"
                                            mb={2}
                                        />
                                        <Text
                                            color="blue.600"
                                            fontWeight="bold"
                                            fontSize="md">
                                            Click to upload{' '}
                                            <span style={{ color: '#666' }}>
                                                or drag and drop
                                            </span>
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            CSV files only
                                        </Text>
                                    </Box>
                                )}
                            </Box>

                            {/* Instructions */}
                            <Box mb={6}>
                                <Text size="lg" fontWeight="bold" mb={2}>
                                    Instructions
                                </Text>
                                <UnorderedList spacing={1}>
                                    <ListItem>
                                        Download the CSV template with your
                                        selected number blocks
                                    </ListItem>
                                    <ListItem>
                                        You can update Partner ID, Company ID,
                                        Company Name (read-only), Carrier IDs,
                                        and Public Pool values
                                    </ListItem>
                                    <ListItem>
                                        To unassign a block from a company,
                                        clear the Company ID field
                                    </ListItem>
                                    <ListItem>
                                        Important: You cannot move blocks
                                        directly between companies. First clear
                                        Company ID, then in a second CSV update
                                        assign to the new company
                                    </ListItem>
                                    <ListItem>
                                        Save your changes to the CSV file and
                                        upload it
                                    </ListItem>
                                    <ListItem>
                                        Review the validation results before
                                        proceeding with the import
                                    </ListItem>
                                </UnorderedList>
                            </Box>

                            {/* Navigation Buttons */}
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mt={4}>
                                <Button
                                    variant="outline"
                                    sx={{ width: '120px', bgColor: 'white' }}
                                    size="sm"
                                    _hover={{ opacity: 0.8 }}
                                    onClick={() => window.history.back()}>
                                    Back
                                </Button>
                                <Button
                                    sx={{ width: '140px' }}
                                    size="sm"
                                    rightIcon={<ChevronRightIcon />}
                                    onClick={() => {
                                        if (uploadedFile) {
                                            handleUploadUsersData({
                                                target: {
                                                    files: [uploadedFile],
                                                },
                                            });
                                        }
                                    }}
                                    isDisabled={!uploadedFile}>
                                    Validate File
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        );
    };

    return <CustomCSVTemplateView />;
};

export default function CSVNumberBlocksUpdate() {
    const {
        handleDownloadCSVTemplate,
        handleUploadData,
        templateLoading,
        verificationChecks,
        uploadData,
        handleExportInvalidData,
        verificationResult,
        payloads,
        error: verificationError,
    } = useCSVNumberBlocksUpdate();

    const {
        viewVerificationResults,
        handleViewVerificationResults,
        handleCancel,
        handleViewAPIResults,
        loadingIndicator,
        viewAPIResults,
        showingAPIProgress,
        handleStartAPIProcess,
    } = useCSVNumberBlocksUpdateUIControl();

    const {
        handlePayloadChange,
        loading: apiLoading,
        counter,
        errorCounter,
        error,
        succeed,
        handleExportFailedData,
        total,
    } = useCSVNumberBlocksBulkUpdate();

    if (loadingIndicator) {
        return (
            <>
                <LoadingIndicator />
            </>
        );
    }

    if (verificationError) {
        return (
            <>
                <VerifyCSVView
                    handleStartOver={handleCancel}
                    errors={verificationError}
                />
            </>
        );
    }

    if (viewAPIResults) {
        return (
            <>
                <APIResultsView
                    error={error}
                    succeed={succeed}
                    handleExportFailedData={handleExportFailedData}
                    handleDone={handleCancel}
                    succeedAPIColumns={succeedAPIColumns}
                    failedAPIColumns={failedAPIColumns}
                />
            </>
        );
    }

    if (showingAPIProgress || apiLoading) {
        return (
            <>
                <APILoadingView
                    completionCounter={counter}
                    payloads={payloads}
                    verificationResult={verificationResult}
                    errorCounter={errorCounter}
                    handleViewAPIResults={handleViewAPIResults}
                    total={total}
                />
            </>
        );
    }

    if (viewVerificationResults) {
        return (
            <>
                <VerifyCSVTableView
                    verificationResult={verificationResult}
                    handleCancel={handleCancel}
                    handleImport={() => {
                        handleStartAPIProcess();
                        handlePayloadChange(payloads);
                    }}
                    passedColumns={passedColumns}
                    failedColumns={failedColumns}
                    handleExportInvalidData={handleExportInvalidData}
                    handleStartOver={handleCancel}
                />
            </>
        );
    }

    if (uploadData) {
        return (
            <>
                <VerifyCSVView
                    handleViewVerificationResults={
                        handleViewVerificationResults
                    }
                    verificationChecks={verificationChecks}
                />
            </>
        );
    }

    return (
        <>
            <NumberBlocksCSVTemplateView
                handleUploadUsersData={handleUploadData}
                handleDownloadTemplate={handleDownloadCSVTemplate}
                loading={templateLoading}
            />
        </>
    );
}
